'use client'

import { Component } from 'react'

import { maybeReloadForChunkError } from '@/lib/analytics/chunk-reload'
import { shouldReportError } from '@/lib/analytics/error-limit'
import type { TrackedEvent } from '@/lib/analytics/events'
import { EventType } from '@/lib/analytics/events'
import { sentryCall } from '@/lib/log/sentry-client'

// Deliberately tracker-token-free path — see src/app/api/ht/route.ts.
const BEACON_PATH = '/api/ht'

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
}

export class AnalyticsErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Shared per-tab cap with the engine's emitError — a crash-looping
    // subtree must not flood site_events/Sentry with identical reports.
    if (!shouldReportError(error.message)) {
      return
    }

    sentryCall((Sentry) =>
      Sentry.captureException(error, {
        contexts: { react: { componentStack: info.componentStack } },
      })
    )

    const tracked: TrackedEvent = {
      event: 'client-error',
      eventType: EventType.Error,
      properties: {
        message: error.message,
        stack: error.stack?.slice(0, 8000),
        componentStack: info.componentStack?.slice(0, 8000),
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      },
      timestamp: Date.now(),
    }

    const payload = {
      events: [],
      allEvents: [tracked],
      rollup: { counts: {}, pageUrl: '', timestamp: Date.now() },
      requestId:
        document.cookie
          .split('; ')
          .find((c) => c.startsWith('ht_request_id='))
          ?.split('=')[1] ?? '',
      distinctId:
        document.cookie
          .split('; ')
          .find((c) => c.startsWith('ph_distinct_id='))
          ?.split('=')[1] ?? '',
      locale: document.documentElement.lang || 'en',
    }

    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon(BEACON_PATH, blob)

    // Deploy-skew chunk failures are curable: one reload fetches HTML that
    // references the current deployment's chunks (sessionStorage-guarded, so
    // never more than one automatic reload per session).
    maybeReloadForChunkError(error.message)
  }

  render() {
    if (this.state.hasError) {
      return this.props.children
    }
    return this.props.children
  }
}
